// 📁 src/lightversion/ProValidation.jsx

import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Stack, Chip } from "@mui/material";
import axios from "../utils/axiosInstance";

const ProValidation = () => {
  const [form, setForm] = useState({
    problemStatement: "",
    userInsight: "",
    direction: "",
  });

  const [isLiteUser, setIsLiteUser] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // ✅ FIXED (correct placement)

  /* ---------------- READ HANDOFF ---------------- */
  useEffect(() => {
    const raw = localStorage.getItem("lite_handoff");
    if (!raw) return;

    try {
      const data = JSON.parse(raw);

      setForm({
        problemStatement: data.problems?.join(", ") || "",
        userInsight: data.reflection || "",
        direction: data.selectedDirection?.title || "",
      });

      setIsLiteUser(true);

      setTimeout(() => {
        localStorage.removeItem("lite_handoff");
      }, 1000);

    } catch (e) {
      console.error("Invalid handoff data");
      localStorage.removeItem("lite_handoff");
    }
  }, []);

  /* ---------------- VALIDATE (REAL API) ---------------- */
  const handleValidate = async () => {
    setLoading(true);
    setError(null); // reset error

    try {
      const res = await axios.post("/lite/validate", {
        problemStatement: form.problemStatement,
        userInsight: form.userInsight,
        direction: form.direction,
      });

      setResult(res.data);

    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 900, mx: "auto" }}>
      
      {/* HEADER */}
      <Typography variant="h4" textAlign="center" fontWeight="bold">
        Validate Your Startup Idea
      </Typography>

      <Typography
        textAlign="center"
        sx={{ mb: 4, color: "text.secondary" }}
      >
        We analyze whether your idea is worth building — before you waste time
      </Typography>

      {/* INPUT SUMMARY */}
      <Stack spacing={2} sx={{ mb: 4 }}>
        <Box>
          <Typography fontWeight="bold">Problem</Typography>
          <Typography>{form.problemStatement || "—"}</Typography>
        </Box>

        <Box>
          <Typography fontWeight="bold">Your Insight</Typography>
          <Typography>{form.userInsight || "—"}</Typography>
        </Box>

        <Box>
          <Typography fontWeight="bold">Direction</Typography>
          <Typography>{form.direction || "—"}</Typography>
        </Box>
      </Stack>

      {/* ERROR */}
      {error && (
        <Typography color="error" textAlign="center" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {/* CTA */}
      {!result && (
        <Box textAlign="center">
          <Button
            variant="contained"
            size="large"
            sx={{ px: 6, py: 1.5 }}
            onClick={handleValidate}
            disabled={loading}
          >
            {loading ? "Analyzing..." : "Run Validation →"}
          </Button>
        </Box>
      )}

      {/* RESULT */}
      {result && (
        <Box
          sx={{
            mt: 5,
            p: 4,
            borderRadius: "16px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
          }}
        >
          <Typography fontWeight="bold" sx={{ mb: 2 }}>
            Validation Summary
          </Typography>

          {/* SCORE */}
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Chip label={`Demand: ${result.demand}`} color="success" />
            <Chip label={`Competition: ${result.competition}`} />
            <Chip label={`Viability: ${result.viability}`} color="primary" />
          </Stack>

          {/* BASIC INSIGHT */}
          <Box
            sx={{
              p: 2,
              borderRadius: "10px",
              background: "#f9fafb",
              mb: 3,
            }}
          >
            <Typography fontSize="14px">
              💡 This idea shows {result.viability} potential based on current signals.
            </Typography>
          </Box>

          {/* DETAILED ANALYSIS */}
          <Box
            sx={{
              p: 3,
              borderRadius: "12px",
              background: "#f3f4f6",
            }}
          >
            <Typography fontWeight="bold" sx={{ mb: 2 }}>
              Detailed Insights
            </Typography>

            <Box
              sx={{
                filter: isLiteUser ? "blur(5px)" : "none",
                pointerEvents: "none",
                userSelect: "none",
              }}
            >
              <Typography><b>Market:</b> {result.analysis?.market || "—"}</Typography>
              <Typography><b>Users:</b> {result.analysis?.users || "—"}</Typography>
              <Typography><b>Risks:</b> {result.analysis?.risks || "—"}</Typography>
              <Typography><b>Opportunity:</b> {result.analysis?.opportunity || "—"}</Typography>
              <Typography><b>MVP:</b> {result.mvp || "—"}</Typography>
            </Box>
          </Box>

          {/* PAYWALL CTA */}
          {isLiteUser && (
            <Box textAlign="center" sx={{ mt: 4 }}>
              <Typography sx={{ mb: 2 }}>
                You have a strong direction. Now validate it properly.
              </Typography>

              <Button
                variant="contained"
                size="large"
                sx={{ px: 5 }}
              >
                Unlock Full Validation →
              </Button>

              <Typography
                variant="caption"
                display="block"
                sx={{ mt: 1, color: "text.secondary" }}
              >
                Takes 5 minutes • No risk
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ProValidation;